import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { Pricing } from '@/app/book/api/useProductLinePricingApi'
import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { ShipRoute } from '@/types/order'

const THIRTY_MINUTES = 1000 * 60 * 30

// Define the GraphQL query
const GET_PRODUCT_LINES = gql`
  query GetProductLines($shipRoute: ShipRouteInput!) {
    getProductLines(shipRoute: $shipRoute) {
      id
      name
      displayName
      icon
      brand {
        id
      }
      packaging {
        id
        name
        default
      }
      insurance(shipRoute: $shipRoute) {
        id
        priceCents
        humanReadableFormat
        coverageAmountCents
        defaultCoverage
        defaultInternational
        defaultDomestic
      }
      labels(shipRoute: $shipRoute) {
        id
        name
        sku
        displayName
        displayWeight
        isDefault: default
        isDefaultInternational: defaultInternational
      }
    }
  }
`
export interface Product {
  type: 'label'
  id: string
  display_name: string
  full_name: string
  name?: string
  sku?: string
  weight: number
  product_line: OrderProductProductLine
  price_cents?: number
  packagingType?: string
}

export interface ProductLinePackagingType {
  id: string
  name: string
  default?: boolean
  display?: boolean
}

export interface ProductLineCoverageOption {
  id: string
  humanReadableFormat: string
  priceCents: number
  coverageAmountCents: number
  defaultCoverage: boolean
  defaultDomestic?: boolean
  defaultInternational?: boolean
}

export interface ProductLine {
  id: string
  name: string
  displayName: string
  icon: string
  brand: {
    id: string
  }
  packaging: ProductLinePackagingType[]
  insurance: ProductLineCoverageOption[]
  labels: {
    id: string
    name: string
    sku?: string
    displayName: string
    displayWeight: number
    isDefault?: boolean
    isDefaultInternational?: boolean
  }[]
}

interface GetProductLinesResponse {
  getProductLines: ProductLine[]
}

/**
 * Converts product lines to product IDs
 * @param productLines - Array of product lines
 * @returns Array of product IDs
 */
export const productLinesToProductIds = (productLines: ProductLine[]): string[] =>
  productLines.flatMap((productLine) => productLine.labels.map((label) => label.id))

export const productLinesToProducts = (
  productLines: ProductLine[],
  productPricing: Pricing[],
): Product[] =>
  productLines.flatMap((line: ProductLine) =>
    line.labels.map(
      ({ id, name, sku, displayName, displayWeight, isDefault, isDefaultInternational }) => ({
        id,
        name,
        sku,
        display_name: displayName,
        full_name: name,
        price_cents: productPricing?.find(({ productId }) => productId === id)?.priceCents,
        product_line: {
          id: line.id,
          name: line.name,
          display_name: line.displayName,
          icon: line.icon,
        },
        brand_id: line.brand.id,
        type: 'label' as const,
        weight: displayWeight,
        packagingTypes: line.packaging,
        isDefault,
        isDefaultInternational,
      }),
    ),
  )

/**
 * Hook to fetch product lines based on the ship route
 * @param shipRoute - The ship route to fetch product lines for
 * @returns Query result containing product lines data
 */
export const useProductLineApi = (shipRoute: ShipRoute): UseQueryResult<ProductLine[], Error> => {
  const client = useGraphQLClient()

  const { city: originCity } = shipRoute.origin
  const { city: destinationCity } = shipRoute.destination

  const fetchProductLines = async (): Promise<ProductLine[]> => {
    const data = await client.request<GetProductLinesResponse>(GET_PRODUCT_LINES, { shipRoute })

    return data.getProductLines.map((productLine) => ({
      ...productLine,
      labels: [...productLine.labels].sort((a, b) => a.displayWeight - b.displayWeight),
    }))
  }

  return useQuery<ProductLine[], Error>({
    queryKey: ['productLines', originCity, destinationCity],
    queryFn: fetchProductLines,
    enabled: !!originCity && !!destinationCity,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
  })
}
